import { generatePricingPlanTitle } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/pricingPlanTitleUtils'
import type { PricingPlan, Validity } from 'Services/ProductManagement/Features/Product/types'
import { IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import { createDurationMap } from 'Util/formatters'

export interface PricingPlanMutationContext {
  activePeriod: Validity | null
  domicileClass: string | undefined
  productId: string
  isSpecialZone?: boolean
}

/**
 * Finds the index of an existing pricing plan matching the given zone map and domicile class.
 * @param pricingPlans - The array of pricing plans to search.
 * @param zoneMap - Array of zoneIds.
 * @param domicileClass - The domicile class to match (or undefined for null).
 * @returns Index of existing pricing plan, or -1 if not found.
 */
export const findExistingPricingPlanIndex = (
  pricingPlans: PricingPlan[],
  zoneMap: string[],
  domicileClass: string | undefined
): number => {
  return pricingPlans.findIndex(
    pricingPlan =>
      zoneMap.length === pricingPlan.zone_map.length &&
      zoneMap.every((zone, index) => zone === pricingPlan.zone_map[index]) &&
      pricingPlan.domicile_class === (domicileClass ?? null)
  )
}

/**
 * Updates an existing pricing plan with the latest price.
 * If no pricing plan exists, creates a new one.
 * If the price is empty and a pricing plan exists:
 *   - In Waltti, for non-special zones with usage_validity_duration set:
 *     only clears the pricings array (keeps the plan since exchange period drives it)
 *   - Otherwise: removes the entire pricing plan.
 * @param zoneMap - The zone map to update or create a pricing plan for.
 * @param price - The latest price for the zone map.
 * @param currentPricingPlans - The array of current pricing plans.
 * @param context - Mutation context containing activePeriod, domicileClass, and productId.
 * @returns The updated array of pricing plans.
 */
export const updateOrCreatePricingPlan = (
  zoneMap: string[],
  price: number | string | null | undefined,
  currentPricingPlans: PricingPlan[],
  context: PricingPlanMutationContext
): PricingPlan[] => {
  const { activePeriod, domicileClass, productId, isSpecialZone } = context
  const existingPricingPlanIndex = findExistingPricingPlanIndex(
    currentPricingPlans,
    zoneMap,
    domicileClass
  )

  if (price === '' || price == null) {
    if (existingPricingPlanIndex > -1) {
      const existing = currentPricingPlans[existingPricingPlanIndex]
      if (
        IS_WALTTI_SERVICE_ENVIRONMENT &&
        !isSpecialZone &&
        existing.usage_validity_duration != null
      ) {
        currentPricingPlans[existingPricingPlanIndex] = { ...existing, pricings: [] }
      } else {
        currentPricingPlans.splice(existingPricingPlanIndex, 1)
      }
    }
    return currentPricingPlans
  }

  if (existingPricingPlanIndex > -1) {
    const existingPricingPlanClone = {
      ...structuredClone(currentPricingPlans[existingPricingPlanIndex]),
      title: generatePricingPlanTitle(currentPricingPlans[existingPricingPlanIndex], productId)
    }
    if (existingPricingPlanClone.pricings.length > 0) {
      existingPricingPlanClone.pricings[0].price = price as number
      existingPricingPlanClone.pricings[0].title = generatePricingPlanTitle(
        existingPricingPlanClone,
        productId
      )
    } else {
      existingPricingPlanClone.pricings[0] = {
        price: price as number,
        currency: 'EUR',
        vat_included: true,
        title: generatePricingPlanTitle(existingPricingPlanClone, productId),
        description: ''
      }
    }

    currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
  } else {
    const newPricingPlanData = {
      active_period: activePeriod,
      visible_period: null,
      domicile_class: domicileClass ?? null,
      custom_fields: {},
      description: '',
      usage_validity_duration: null,
      zone_map: zoneMap
    } as PricingPlan
    currentPricingPlans.push({
      ...newPricingPlanData,
      pricings: [
        {
          price: price as number,
          currency: 'EUR',
          vat_included: true,
          title: generatePricingPlanTitle(newPricingPlanData, productId),
          description: ''
        }
      ],
      title: generatePricingPlanTitle(newPricingPlanData, productId)
    })
  }

  return currentPricingPlans
}

/**
 * Updates an existing pricing plan's exchange period (usage_validity_duration).
 * Only Waltti environment has exchange periods enabled.
 * In Waltti environment: creates a new pricing plan with empty pricings if none exists,
 * and removes the entire plan when the value is cleared.
 * @param zoneMap - The zone map to update or create a pricing plan for.
 * @param validityLength - The validity length in minutes.
 * @param currentPricingPlans - The array of current pricing plans.
 * @param context - Mutation context containing activePeriod, domicileClass, and productId.
 * @returns The updated array of pricing plans.
 */
export const updateOrCreatePricingExchangePeriod = (
  zoneMap: string[],
  validityLength: number | string | null | undefined,
  currentPricingPlans: PricingPlan[],
  context: PricingPlanMutationContext
): PricingPlan[] => {
  const { activePeriod, domicileClass, productId } = context
  const existingPricingPlanIndex = findExistingPricingPlanIndex(
    currentPricingPlans,
    zoneMap,
    domicileClass
  )

  if (validityLength === '' || validityLength == null) {
    if (existingPricingPlanIndex > -1) {
      // Removing exchange period removes the entire pricing plan
      // since exchange period is the primary/driving field
      currentPricingPlans.splice(existingPricingPlanIndex, 1)
    }
    return currentPricingPlans
  }

  if (existingPricingPlanIndex > -1) {
    const existingPricingPlanClone = structuredClone(currentPricingPlans[existingPricingPlanIndex])
    existingPricingPlanClone.usage_validity_duration = createDurationMap(
      validityLength as number
    ).minutes
    currentPricingPlans[existingPricingPlanIndex] = existingPricingPlanClone
  } else {
    // Create a new pricing plan with empty pricings when setting exchange period
    const newPricingPlanData = {
      active_period: activePeriod,
      visible_period: null,
      domicile_class: domicileClass ?? null,
      custom_fields: {},
      description: '',
      usage_validity_duration: createDurationMap(validityLength as number).minutes,
      zone_map: zoneMap
    } as PricingPlan
    currentPricingPlans.push({
      ...newPricingPlanData,
      pricings: [],
      title: generatePricingPlanTitle(newPricingPlanData, productId)
    })
  }

  return currentPricingPlans
}
